import React, { useState, useCallback, useContext } from 'react';
import { createUseStyles, Input, Textarea, Button, Advice } from '@Sage/hoi-poi-ui';
import { URL_REGEX } from 'utils/string';
import { a2aInfo } from 'lib/services/copilotAgents';
import { CopilotContext } from '../../../Context';
import CopilotSidebarTabHeader from '../../components/CopilotSidebarTabHeader';

import styles from './styles';
const useStyles = createUseStyles(styles, 'CopilotSidebarAgentsRemoteForm');

const CopilotSidebarAgentsRemoteForm = ({ agent, onCancel, onSave }) => {
    const classes = useStyles();
    const { refreshAgents } = useContext(CopilotContext);
    const [isLoading, setIsLoading] = useState(false);
    const [url, setUrl] = useState(agent?.url);
    const [info, setInfo] = useState(
        agent
            ? {
                  name: agent?.name,
                  description: agent?.description,
              }
            : undefined,
    );
    const [error, setError] = useState(null);

    const onInnerCancel = useCallback(() => {
        onCancel();
        setTimeout(() => {
            setUrl();
            setError();
            setInfo();
        });
    }, [onCancel]);

    const loadURL = useCallback(() => {
        if (url && !URL_REGEX.test(url)) {
            setError('Agent URL is not valid');
            setInfo();
        } else if (url) {
            setError(null);
            a2aInfo(url)
                .then(({ data }) => {
                    setInfo(data);
                })
                .catch((err) => {
                    console.error(err);
                    setError(
                        'Error fetching agent info. Please provide a compatible agent URL source.',
                    );
                });
        }
    }, [url]);

    const onInnerSave = useCallback(() => {
        setIsLoading(true);
        onSave({ url })
            .then(() => {
                onInnerCancel();
            })
            .finally(() => {
                setIsLoading(false);
                refreshAgents();
            });
    }, [onInnerCancel, onSave, refreshAgents, url]);

    return (
        <div className={classes.root}>
            <CopilotSidebarTabHeader
                title={agent?.id ? 'Edit agent' : 'Create agent'}
                onBack={onInnerCancel}
                sideComponent={
                    <Button
                        type="primary"
                        onClick={onInnerSave}
                        isLoading={isLoading}
                        isDisabled={!url}
                    >
                        Save
                    </Button>
                }
            />
            <Advice
                type="semanticInfo"
                title="✨ Let's bring your agent to life!"
                showIcon
                defaultCollapsed={false}
            >
                Connect an existing agent by pasting its URL! 🚀 We&apos;ll automatically fetch the
                details from A2A-compatible sources.
            </Advice>
            <Input
                label="URL Agent"
                placeholder="Paste here the URL where you create the agent"
                value={url}
                onChange={setUrl}
                onEnter={loadURL}
                onBlur={loadURL}
                error={error}
                isFullWidth
                isRequired
            />
            {info && (
                <>
                    <Input label="Name" value={info.name} isFullWidth isReadOnly />
                    <Textarea
                        label="Description"
                        value={info.description}
                        minRows={2}
                        maxRows={4}
                        isResizable={false}
                        isFullWidth
                        isReadOnly
                    />
                </>
            )}
        </div>
    );
};

export default CopilotSidebarAgentsRemoteForm;
